import { ApolloLink, type Operation } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { print } from 'graphql';

import { sendAuthenticated } from 'app/utils/hyrox-mirror/authenticated';
import type { MirrorAccess, MirrorSession, MirrorSessionFailure } from 'app/utils/hyrox-mirror/session';
import { MIRRORED_OPERATIONS, type MirrorLookups, translateVariables } from 'app/utils/hyrox-mirror/translate';
import logger from 'utils/logger';

export type MirrorReportKind = MirrorSessionFailure | 'mirror_skipped' | 'mirror_failed';

interface CreateHyroxMirrorLinkArgs {
  graphqlUrl: string;
  session: MirrorSession;
  lookups: MirrorLookups;
  getTimezoneHeader: () => Record<string, string>;
}

type ReportContext = Record<string, string | number | string[]>;

// The queue holds each pending Operation — query AST and variables, and PatchUser's variables carry a
// plaintext password — so it must not grow without bound. A depth this large is already pathological: it
// means replays are failing to drain, which the drop itself reports.
const MAX_QUEUE_DEPTH = 50;

const isMirroredMutation = ({ query, operationName }: Operation): boolean => {
  const definition = getMainDefinition(query);

  return (
    definition.kind === 'OperationDefinition' &&
    definition.operation === 'mutation' &&
    MIRRORED_OPERATIONS.has(operationName)
  );
};

// Replays allow-listed mutations to hyrox-api after the primary (one-fiit) result arrives. The primary
// result passes through untouched; the mirror runs off the render path, in order, and only reports.
export const createHyroxMirrorLink = ({
  graphqlUrl,
  session,
  lookups,
  getTimezoneHeader,
}: CreateHyroxMirrorLinkArgs): ApolloLink => {
  const queue: { tail: Promise<void>; depth: number } = { tail: Promise.resolve(), depth: 0 };
  const reportedOnce = new Set<string>();

  // Context is operation names, ids and reasons only — never the variables (PatchUser carries a password)
  const report = (kind: MirrorReportKind, operationName: string, context: ReportContext = {}, error?: unknown) => {
    const message = `hyrox-mirror: ${kind}`;
    const fullContext = { ...context, kind, operationName };

    if (kind === 'mirror_failed') {
      logger.error({ message, error, context: fullContext });
    } else {
      logger.warn({ message, context: fullContext });
    }
  };

  // For conditions that hold for a whole page session rather than for one write. Reporting those per
  // mutation scales Sentry volume with user activity for a single known fact, and would drown the
  // hyrox-mirror:* alert in exactly the population it is least useful for.
  const reportOnce = (key: string, kind: MirrorReportKind, operationName: string, context: ReportContext) => {
    if (reportedOnce.has(key)) {
      return;
    }

    reportedOnce.add(key);
    report(kind, operationName, context);
  };

  // A queued replay outlives the login that produced it: the queue is serial and each job can wait on
  // lookups and a token refresh, so a sign-out and a second sign-in can both happen before it runs. The
  // hyrox identity is resolved at run time, so without this check the mirror would write the first user's
  // save, completion or profile change (PatchUser carries a name and a password) onto the second user's
  // hyrox record. The replay is bound instead to the login it was captured under, and dropped if that
  // login is gone — a re-login as the same user counts as gone too, which costs a reported skip rather
  // than a wrong write. Same-user cross-tab logins are dropped for the same reason.
  const mirror = async (operation: Operation, capturedSessionId: string | null): Promise<void> => {
    const { operationName, query, variables } = operation;
    const sameLogin = (): boolean => session.getSessionId() === capturedSessionId;

    // No hyrox session was ever minted for this login. Only the Cognito SSO path can mint one, so a
    // password sign-in, a one-fiit authorization-code sign-in, and any session that predates this deploy
    // mirror nothing — a documented coverage boundary (README), not a per-write fault. It holds for every
    // mutation the page session makes, so it is reported once rather than once per write.
    if (capturedSessionId === null) {
      return reportOnce('no_mirror_session', 'mirror_session_unavailable', operationName, {
        reason: 'no_mirror_session',
      });
    }

    if (!sameLogin()) {
      return report('mirror_skipped', operationName, { reason: 'session_changed' });
    }

    const access = await session.getAccessToken();

    if (!access.ok) {
      return report(access.reason, operationName);
    }

    const hyroxUserId = session.getUserId();

    if (hyroxUserId === null) {
      return report('mirror_session_unavailable', operationName);
    }

    const translated = await translateVariables(operationName, variables, hyroxUserId, lookups);

    if (!translated.ok) {
      return report('mirror_skipped', operationName, { reason: translated.reason });
    }

    // Re-checked after the lookups, which are network round trips of their own
    if (!sameLogin()) {
      return report('mirror_skipped', operationName, { reason: 'session_changed' });
    }

    const sent = await sendAuthenticated({
      // Pinned to the captured login: a sign-out landing between the check above and the send, or during
      // the send's own refresh-and-retry, must not let this replay pick up the next user's token
      getAccessToken: (): Promise<MirrorAccess> =>
        sameLogin() ? session.getAccessToken() : Promise.resolve({ ok: false, reason: 'mirror_session_unavailable' }),
      refreshAccessToken: (): Promise<string | null> =>
        sameLogin() ? session.refreshAccessToken() : Promise.resolve(null),
      request: {
        headers: getTimezoneHeader(),
        keepalive: true,
        operationName,
        query: print(query),
        url: graphqlUrl,
        variables: translated.variables,
      },
    });

    if (!sent.ok) {
      return report(sent.reason, operationName);
    }

    if (sent.response.errors?.length) {
      return report('mirror_failed', operationName, { errors: sent.response.errors.map(({ message }) => message) });
    }
  };

  // Serial on purpose: save→unsave→save must land on hyrox in that order, and hyrox refresh tokens are
  // one-time-use so two mirrors must never race a refresh
  const enqueue = (operation: Operation): void => {
    // Runs inside the primary result path, so nothing here may throw: localStorage being unavailable would
    // otherwise propagate out of .map() and surface a mutation that actually succeeded as failed
    let capturedSessionId: string | null;

    try {
      // Read now, not when the job runs — that is the whole point of the check inside mirror()
      capturedSessionId = session.getSessionId();
    } catch (error: unknown) {
      report('mirror_failed', operation.operationName, {}, error);
      return;
    }

    if (queue.depth >= MAX_QUEUE_DEPTH) {
      report('mirror_skipped', operation.operationName, { reason: 'queue_full', depth: queue.depth });
      return;
    }

    queue.depth += 1;

    const job = () =>
      mirror(operation, capturedSessionId)
        .catch((error: unknown) => report('mirror_failed', operation.operationName, {}, error))
        .finally(() => {
          queue.depth -= 1;
        });

    queue.tail = queue.tail.then(job, job);
  };

  return new ApolloLink((operation, forward) =>
    forward(operation).map((result) => {
      if (isMirroredMutation(operation) && !result.errors?.length) {
        enqueue(operation);
      }

      return result;
    }),
  );
};
